import {notifications} from '@mantine/notifications';
import React from 'react';
import type {
  TaskAssignmentSimpleResponse,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {useGetTaskAssignmentsListQuery} from 'src/redux/queries/taskAssignment';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {
  TASK_ASSIGNMENT_OPEN_STATES,
  TaskAssignmentPillConfig,
  taskRowSubText,
} from 'src/app/tasks/taskHelpers';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import useDebounce from 'src/shared/hooks/useDebounce';

const maxPageRows = 10;

export interface SupervisorApprovalAssignmentsListProps {
  taskType: TaskTypeEnum;
  taskId: number;
  refetchId: number;
}

export const SupervisorApprovalAssignmentsList: React.FC<
  SupervisorApprovalAssignmentsListProps
> = ({taskId, taskType, refetchId}) => {
  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: `supervisor-approval-assignments-${taskType}-${taskId}`,
    pageSize: maxPageRows,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const assignmentStates = TASK_ASSIGNMENT_OPEN_STATES;

  const {
    data: listResponse,
    isLoading,
    refetch,
  } = useGetTaskAssignmentsListQuery({
    taskType: taskType,
    taskId: taskId,
    assignmentStates: assignmentStates,
    start,
    length,
    search: debouncedSearchTerm || undefined,
  });

  React.useEffect(() => {
    if (refetchId > 0) {
      void refetch();
    }
  }, [refetchId]);

  const indexListData: TaskAssignmentSimpleResponse[] | undefined =
    listResponse?.data;
  const totalRecords = listResponse?.totalRecords ?? 0;

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  return (
    <ContentWrapper>
      <DataHeader
        titleText={''}
        searchTerm={searchTerm}
        filters={[]}
        onSearchTermChange={handleSearchTerm}
        isFilterActive={false}
      />
      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={undefined}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={() =>
          notifications.show({
            title: 'Invalid Action',
            message:
              'You cannot open a task assignment record from the supervisor approval screen.',
            color: 'yellow',
          })
        }
        onPageChange={handlePageChange}
        onGetPillMapping={(item: TaskAssignmentSimpleResponse) =>
          TaskAssignmentPillConfig(
            item.state,
            item.taskDefinition.taskType,
            item.taskDefinition.state,
            item.dueAt
          )
        }
        onRowTextRender={(item: TaskAssignmentSimpleResponse) =>
          item.assignedToUser?.name
        }
        onRowSubTextRender={taskRowSubText}
        hasSearchTerm={searchTerm.trim().length > 0}
        hasCreationLogic={false}
      />
    </ContentWrapper>
  );
};
